import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Tag, Loader2, Search, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import { CATEGORY_TO_HUB_SLUG, BLOG_CATEGORY_HUB_MAP, BLOG_CATEGORY_HUBS } from "../data/blogCategoryHubs";

export default function Blog() {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState("all");
    const [query, setQuery] = useState("");

    useEffect(() => {
        api.get("/blogs")
            .then((r) => setBlogs(r.data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => {
        const c = ["all"];
        blogs.forEach((b) => { if (b.category && !c.includes(b.category)) c.push(b.category); });
        return c;
    }, [blogs]);

    const filtered = useMemo(() => {
        let list = blogs;
        if (activeCat !== "all") list = list.filter((b) => b.category === activeCat);
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            list = list.filter((b) =>
                b.title.toLowerCase().includes(q) ||
                (b.excerpt || "").toLowerCase().includes(q) ||
                (b.tags || []).some((t) => t.toLowerCase().includes(q))
            );
        }
        return list;
    }, [blogs, activeCat, query]);

    const featured = filtered[0];
    const rest = filtered.slice(1);

    return (
        <div data-testid="blog-page">
            <SEO
                title="Tuition & Education Blog — Strategies, Board Guides & Exam Tips"
                description="Expert articles on home tuition, 1:1 online classes, CBSE/ICSE/IGCSE/IB board strategies, NEET & JEE preparation, parent guides and study tips. Updated weekly by Anand Tutorials senior mentors."
                keywords="tuition blog India, CBSE preparation strategy, ICSE tips, online tuition guide, NEET strategy 2026, JEE preparation tips, home tutor guide, personalised learning, NRI online tuition"
                path="/blog"
            />
            <PageHeader
                eyebrow="Insights & Guides · Updated weekly"
                title="The Anand Tutorials Blog"
                subtitle="Expert articles by senior mentors — board strategies, NEET / JEE roadmaps, parent guides, and the data behind 1-on-1 personalised learning."
            />

            <section className="py-10 lg:py-12 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    {/* search + categories */}
                    <div className="flex flex-col md:flex-row gap-4 mb-8 items-start md:items-center justify-between">
                        <div className="relative w-full md:max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search articles, topics, tags…"
                                data-testid="blog-search"
                                className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                            />
                        </div>
                        <div className="flex flex-wrap gap-1.5 overflow-x-auto pb-1">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setActiveCat(c)}
                                    data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                                        activeCat === c
                                            ? "bg-[#1E3A8A] text-white"
                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                    }`}
                                >
                                    {c === "all" ? "All Articles" : c}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Batch 2J — surface dedicated topic hub when the active filter has one */}
                    {activeCat !== "all" && CATEGORY_TO_HUB_SLUG[activeCat] && BLOG_CATEGORY_HUB_MAP[CATEGORY_TO_HUB_SLUG[activeCat]] && (
                        <Link
                            to={`/blog/category/${CATEGORY_TO_HUB_SLUG[activeCat]}`}
                            data-testid={`blog-view-hub-${CATEGORY_TO_HUB_SLUG[activeCat]}`}
                            className="group inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 px-4 py-2.5 rounded-xl text-sm font-semibold mb-6 transition-all"
                        >
                            <Sparkles className="w-4 h-4" />
                            View the dedicated {BLOG_CATEGORY_HUB_MAP[CATEGORY_TO_HUB_SLUG[activeCat]].label} topic hub — intro guide + all articles
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    )}

                    {loading ? (
                        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-20 text-slate-500" data-testid="blog-empty">
                            No articles found{query ? ` for "${query}"` : ""}. Try clearing filters.
                        </div>
                    ) : (
                        <>
                            {/* Featured post */}
                            {featured && (
                                <Link
                                    to={`/post/${featured.slug}`}
                                    data-testid={`blog-featured-${featured.slug}`}
                                    className="group grid md:grid-cols-2 gap-6 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] rounded-3xl overflow-hidden hover:shadow-2xl transition-all mb-10"
                                >
                                    {featured.cover_image && (
                                        <div className="aspect-[16/10] md:aspect-auto overflow-hidden">
                                            <img src={featured.cover_image} alt={featured.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        </div>
                                    )}
                                    <div className="p-8 lg:p-10 text-white flex flex-col justify-center">
                                        <div className="inline-flex items-center gap-1.5 bg-amber-500 text-[#0F172A] text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full self-start mb-4">
                                            <Sparkles className="w-3 h-3" /> Featured
                                        </div>
                                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-3 group-hover:text-amber-300 transition-colors">{featured.title}</h2>
                                        <p className="text-blue-100 leading-relaxed mb-5 line-clamp-3">{featured.excerpt}</p>
                                        <div className="flex items-center gap-4 text-xs text-blue-200 uppercase tracking-[0.15em] mb-5">
                                            <span>{featured.category}</span>
                                            <span>·</span>
                                            <span>{new Date(featured.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                                        </div>
                                        <div className="inline-flex items-center gap-2 text-amber-400 font-semibold group-hover:gap-3 transition-all">
                                            Read full article <ArrowRight className="w-4 h-4" />
                                        </div>
                                    </div>
                                </Link>
                            )}

                            {/* Rest of articles */}
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                                {rest.map((b) => (
                                    <Link
                                        key={b.id}
                                        to={`/post/${b.slug}`}
                                        data-testid={`blog-card-${b.slug}`}
                                        className="group bg-[#F8FAFC] rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:border-blue-200 transition-all flex flex-col"
                                    >
                                        {b.cover_image && (
                                            <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                                                <img src={b.cover_image} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            </div>
                                        )}
                                        <div className="p-6 flex flex-col flex-1">
                                            <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-[#1E3A8A] rounded font-semibold">{b.category || "General"}</span>
                                                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                                            </div>
                                            <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-2 group-hover:text-[#1E3A8A] leading-snug">{b.title}</h3>
                                            <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">{b.excerpt}</p>
                                            <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A8A] group-hover:gap-2.5 transition-all">
                                                Read article <ArrowRight className="w-4 h-4" />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* Newsletter / lead magnet */}
            <section className="py-12 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-10"></div>
                <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Explore the blog by topic hub</h3>
                        <p className="text-blue-100 max-w-2xl mx-auto">Each hub gathers every article in a specific topic — city guides, board strategy, competitive-exam roadmaps, NRI transitions and more.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10" data-testid="blog-hubs-grid">
                        {BLOG_CATEGORY_HUBS.map((h) => (
                            <Link
                                key={h.slug}
                                to={`/blog/category/${h.slug}`}
                                data-testid={`blog-hub-${h.slug}`}
                                className="group flex items-center justify-between gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-400 rounded-xl px-4 py-3 transition-all"
                            >
                                <span className="font-heading font-semibold text-white text-sm group-hover:text-amber-300 truncate">{h.label}</span>
                                <ArrowRight className="w-4 h-4 text-amber-400 shrink-0" />
                            </Link>
                        ))}
                    </div>
                    <div className="text-center">
                        <Link to="/register" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-bold px-6 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                            Book Free Demo <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
